/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */
import shaka from "shaka-player";

import { PlayerTracker } from "../../types";
import {
  PlayerDimension,
  PlayerResizeCallback,
  PlayheadTracker,
} from "../types";

import { RESIZE_CHECK_INTERVAL, UPDATE_TIME_INTERVAL } from "./consts";

export class PlayerTrackerShaka implements PlayerTracker {
  private updateTimeIntervalHandle?: number = undefined;
  private resizeCheckIntervalHandle?: number = undefined;
  private playerEventsManager: shaka.util.EventManager;

  private readonly onPlayerResize: PlayerResizeCallback;

  private playheadTracker: PlayheadTracker = {
    currentTime: 0,
    previousTime: 0,
    duration: 0,
  };

  private playerDimension: PlayerDimension;

  private readonly player: shaka.Player;
  constructor(
    player: shaka.Player,
    onPlayerResize: PlayerResizeCallback,
    playerEventsManager: shaka.util.EventManager,
  ) {
    this.player = player;
    this.onPlayerResize = onPlayerResize;
    this.playerEventsManager = playerEventsManager;

    this.playerDimension = {
      width: this.getPlayerWidth(),
      height: this.getPlayerHeight(),
    };
    this.playerEventsManager.listenOnce(
      this.player.getMediaElement(),
      "play",
      this.initialize,
    );
  }

  get playhead() {
    return this.playheadTracker;
  }

  get dimension() {
    return this.playerDimension;
  }

  initialize = () => {
    this.clearIntervals();

    this.updateTimeIntervalHandle = window.setInterval(
      this.updateCurrentTime,
      UPDATE_TIME_INTERVAL,
    );

    this.resizeCheckIntervalHandle = window.setInterval(
      this.checkForResize,
      RESIZE_CHECK_INTERVAL,
    );
  };

  getPlayerWidth(): number {
    const videoElement: any = this.player.getMediaElement();
    if (!videoElement) {
      return 0;
    }
    const rect = videoElement.getBoundingClientRect();
    return rect.width || videoElement.clientWidth;
  }

  getPlayerHeight(): number {
    const videoElement: any = this.player.getMediaElement();
    if (!videoElement) {
      return 0;
    }
    const rect = videoElement.getBoundingClientRect();
    return rect.height || videoElement.clientHeight;
  }

  reset() {
    this.clearIntervals();
    this.playerEventsManager.listenOnce(
      this.player.getMediaElement(),
      "play",
      this.initialize,
    );
  }

  private updateCurrentTime = () => {
    this.playheadTracker.currentTime =
      this.player?.getMediaElement?.()?.currentTime || 0;
  };

  dispose() {
    this.clearIntervals();
  }

  private clearIntervals = () => {
    clearInterval(this.updateTimeIntervalHandle);
    clearInterval(this.resizeCheckIntervalHandle);
  };

  private checkForResize = () => {
    const currentWidth = this.getPlayerWidth();
    const currentHeight = this.getPlayerHeight();

    if (
      currentWidth !== this.playerDimension.width ||
      currentHeight !== this.playerDimension.height
    ) {
      this.playerDimension.width = currentWidth;
      this.playerDimension.height = currentHeight;
      this.onPlayerResize(currentWidth, currentHeight);
    }
  };
}
